/**
 * GoalsView — the operator surface for the Orchestrator.
 *
 * Give Argo a goal from the deterministic template registry and it plans the
 * steps and runs them end-to-end under governance: read steps execute
 * automatically, every write step pauses at the SAME approvals gate as
 * everything else. This view is pure UI + wiring — start a run, watch its
 * plan advance step-by-step (live via subscribeRuns), and approve/dismiss
 * the gated step inline so the run progresses immediately.
 *
 * Honest by construction: runs render only what the store holds (goalLabel,
 * step statuses, real results), and the empty state says so plainly.
 */

import { useEffect, useState } from 'react';
import { Target, Play, Bot, Check, CheckCheck, X, Ban } from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { LoadingState, EmptyState } from '../components/States';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  GOAL_TEMPLATES,
  startRun,
  advanceRun,
  approveRun,
  cancelRun,
  subscribeRuns,
} from '../lib/agent/orchestrator';
import { resolveApproval } from '../lib/agent/approvals';
import { actionRisk } from '../lib/agent/actions';
import { proposeSummary } from '../lib/agent/runOutcome';
import type { OrchestratorRun, PlanStep } from '../lib/db/types';
import type { ViewKey } from '../components/TopMenu';

// --- Presentation maps -------------------------------------------------------

const RUN_STATUS_META: Record<OrchestratorRun['status'], { label: string; pill: string }> = {
  running: { label: 'Running', pill: 'glass-pill glass-pill--blue' },
  awaiting_approval: { label: 'Awaiting approval', pill: 'glass-pill glass-pill--orange' },
  done: { label: 'Done', pill: 'glass-pill glass-pill--green' },
  cancelled: { label: 'Cancelled', pill: 'glass-pill' },
};

const STEP_STATUS_META: Record<PlanStep['status'], { label: string; pill: string }> = {
  pending: { label: 'Pending', pill: 'glass-pill' },
  awaiting_approval: { label: 'Awaiting approval', pill: 'glass-pill glass-pill--orange' },
  done: { label: 'Done', pill: 'glass-pill glass-pill--green' },
  skipped: { label: 'Skipped', pill: 'glass-pill' },
  failed: { label: 'Failed', pill: 'glass-pill glass-pill--magenta' },
};

const STEP_KIND_META: Record<PlanStep['kind'], { label: string; pill: string }> = {
  read: { label: 'Read', pill: 'glass-pill glass-pill--blue' },
  write: { label: 'Write', pill: 'glass-pill glass-pill--magenta' },
};

// Risk tier for a write step's proposed action — display only (execution is
// unchanged; approveRun/advanceRun own the actual gating).
const RISK_META: Record<ReturnType<typeof actionRisk>, { label: string; pill: string }> = {
  auto: { label: 'Auto (reversible)', pill: 'glass-pill glass-pill--green' },
  gated: { label: 'Needs approval', pill: 'glass-pill glass-pill--orange' },
};

/** Write steps that still have work left (not yet done/skipped/failed). */
function pendingWrites(run: OrchestratorRun): PlanStep[] {
  return run.steps.filter(
    (s) => s.kind === 'write' && (s.status === 'pending' || s.status === 'awaiting_approval'),
  );
}

function fmtWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ===========================================================================

export function GoalsView({ onNavigate }: { onNavigate?: (view: ViewKey) => void }) {
  const { hasRole } = useAuth();
  const canMutate = hasRole('staff');

  const [runs, setRuns] = useState<OrchestratorRun[]>([]);
  // First-snapshot flag: distinguishes "still loading" from "loaded, empty"
  // so we show a skeleton instead of a premature empty state.
  const [loaded, setLoaded] = useState(false);
  // Which template is currently starting (disables its Run button).
  const [startingId, setStartingId] = useState<string | null>(null);
  // Which step's approve/dismiss is in flight (disables that step's buttons).
  const [decidingStepId, setDecidingStepId] = useState<string | null>(null);
  // Which run's plan-level batch approval is in flight (disables its buttons).
  const [approvingRunId, setApprovingRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    // subscribeRuns already sorts newest-first.
    const unsub = subscribeRuns((records) => {
      if (active) {
        setRuns(records);
        setLoaded(true);
      }
    });
    return () => {
      active = false;
      unsub();
    };
  }, []);

  async function onStart(goalId: string) {
    setStartingId(goalId);
    setError(null);
    try {
      await startRun(goalId);
    } catch {
      setError('Starting the goal failed — try again.');
    } finally {
      setStartingId(null);
    }
  }

  async function onDecide(run: OrchestratorRun, step: PlanStep, decision: 'approved' | 'dismissed') {
    if (!step.approvalId) return;
    setDecidingStepId(step.id);
    setError(null);
    try {
      await resolveApproval(step.approvalId, decision);
      // Re-drive the run so it advances past the resolved gate immediately.
      await advanceRun(run.id);
    } catch {
      setError('Resolving the step failed — try again.');
    } finally {
      setDecidingStepId(null);
    }
  }

  async function onApprovePlan(run: OrchestratorRun) {
    if (!canMutate) return;
    setApprovingRunId(run.id);
    setError(null);
    try {
      await approveRun(run.id);
    } catch {
      setError('Approving the plan failed — try again.');
    } finally {
      setApprovingRunId(null);
    }
  }

  async function onCancel(run: OrchestratorRun) {
    setError(null);
    try {
      await cancelRun(run.id);
    } catch {
      setError('Cancelling the run failed — try again.');
    }
  }

  return (
    <div className="app-container" style={{ display: 'grid', gap: 16 }}>
      {/* Header */}
      <div>
        <span className="t-eyebrow">Goals</span>
        <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
          Goals
        </h1>
        <p className="t-meta" style={{ margin: '6px 0 0' }}>
          Give Argo a goal; it plans the steps and runs them under your approval.
        </p>
      </div>

      {/* Start a goal */}
      <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: C.magenta,
            }}
          >
            <Target size={17} strokeWidth={2} />
          </span>
          <div>
            <span className="t-eyebrow">Orchestrator</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              Start a goal
            </h2>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 10,
          }}
        >
          {GOAL_TEMPLATES.map((t) => (
            <GlassPanel
              key={t.id}
              variant="subtle"
              animate={false}
              style={{
                padding: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <span className="t-card-title" style={{ display: 'block' }}>
                {t.label}
              </span>
              <p className="t-meta" style={{ margin: 0, flex: 1, color: C.textMuted }}>
                {t.description}
              </p>
              {canMutate ? (
                <button
                  type="button"
                  className="glass-btn glass-btn--primary"
                  onClick={() => void onStart(t.id)}
                  disabled={startingId !== null}
                  title="Plans the steps from your real data and pauses at every write for your approval."
                  style={{
                    alignSelf: 'flex-start',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <Play size={13} /> {startingId === t.id ? 'Planning…' : 'Run'}
                </button>
              ) : (
                <span className="glass-pill" title="Read-only access — editors only" style={{ alignSelf: 'flex-start' }}>
                  Read-only
                </span>
              )}
            </GlassPanel>
          ))}
        </div>
      </GlassPanel>

      {/* Runs */}
      <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(0,130,243,0.20), rgba(0,200,120,0.10))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: C.blueElectric,
            }}
          >
            <Bot size={17} strokeWidth={2} />
          </span>
          <div>
            <span className="t-eyebrow">Governed execution</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              Runs
            </h2>
          </div>
        </div>

        {error && (
          <p className="t-meta" role="alert" style={{ margin: '0 0 10px', color: C.magenta }}>
            {error}
          </p>
        )}

        {!loaded ? (
          <LoadingState rows={3} label="Loading runs" />
        ) : runs.length === 0 ? (
          <EmptyState
            icon={<Target size={22} strokeWidth={1.75} />}
            title="No goals run yet. Start one above."
          />
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {runs.map((run) => (
              <RunCard
                key={run.id}
                run={run}
                canMutate={canMutate}
                decidingStepId={decidingStepId}
                approving={approvingRunId === run.id}
                onDecide={(step, decision) => void onDecide(run, step, decision)}
                onApprovePlan={() => void onApprovePlan(run)}
                onCancel={() => void onCancel(run)}
                onReview={onNavigate ? () => onNavigate('dashboard') : undefined}
              />
            ))}
          </div>
        )}
      </GlassPanel>
    </div>
  );
}

// --- One run: header pill + step list ---------------------------------------

function RunCard({
  run,
  canMutate,
  decidingStepId,
  approving,
  onDecide,
  onApprovePlan,
  onCancel,
  onReview,
}: {
  run: OrchestratorRun;
  canMutate: boolean;
  decidingStepId: string | null;
  approving: boolean;
  onDecide: (step: PlanStep, decision: 'approved' | 'dismissed') => void;
  onApprovePlan: () => void;
  onCancel: () => void;
  /** Navigate to the Dashboard approvals inbox. Omitted → chip hidden. */
  onReview?: () => void;
}) {
  const meta = RUN_STATUS_META[run.status];
  const inFlight = run.status === 'running' || run.status === 'awaiting_approval';
  // Remaining write work → the plan-level batch approval is offered.
  const remainingWrites = pendingWrites(run);
  const canApprovePlan = canMutate && inFlight && remainingWrites.length > 0;
  // One-line plan summary: risk tiers across ALL write steps in the plan.
  const writeSteps = run.steps.filter((s) => s.kind === 'write' && s.action);
  const autoCount = writeSteps.filter((s) => actionRisk(s.action!) === 'auto').length;
  const gatedCount = writeSteps.length - autoCount;
  // The propose outcome: write steps ARE the proposals this run queued.
  const propose = proposeSummary(run);

  return (
    <GlassPanel variant="subtle" animate={false} style={{ padding: '14px 16px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 10,
        }}
      >
        <span className="t-card-title">{run.goalLabel}</span>
        <span className={meta.pill}>{meta.label}</span>
        <span className="t-meta" style={{ color: C.textMuted }}>
          Started {fmtWhen(run.createdAt)}
        </span>
        {canMutate && inFlight && (
          <span
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {canApprovePlan && (
              <button
                type="button"
                className="glass-btn glass-btn--primary"
                onClick={onApprovePlan}
                disabled={approving}
                title="Approve the whole plan — every remaining write runs under this one approval."
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <CheckCheck size={12} /> {approving ? 'Approving…' : 'Approve plan'}
              </button>
            )}
            <button
              type="button"
              className="glass-btn"
              onClick={onCancel}
              disabled={approving}
              title="Cancel the run — remaining steps are skipped."
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              <Ban size={12} /> Cancel
            </button>
          </span>
        )}
      </div>

      {writeSteps.length > 0 && (
        <p className="t-meta" style={{ margin: '0 0 8px', color: C.textMuted }}>
          {run.steps.length} steps · {autoCount} auto · {gatedCount} need approval
        </p>
      )}

      {/* PROPOSE outcome — the queued proposals, made visible. The write steps
          ARE the proposals, so this reflects real queued work (or "ran clean"). */}
      <p
        className="t-meta"
        style={{
          margin: '0 0 8px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          color: C.text,
        }}
      >
        <span className="glass-pill glass-pill--magenta" style={{ fontSize: 10, padding: '2px 8px' }}>
          Propose
        </span>
        <span>{propose.text}</span>
        {propose.showReviewChip && onReview && (
          <button
            type="button"
            className="glass-pill glass-pill--orange"
            onClick={onReview}
            title="Open the Dashboard approvals inbox to review these queued proposals"
            style={{ fontSize: 10, padding: '2px 8px', cursor: 'pointer' }}
          >
            Review in Approvals
          </button>
        )}
      </p>

      <div style={{ display: 'grid', gap: 6 }}>
        {run.steps.map((step) => {
          const kind = STEP_KIND_META[step.kind];
          const status = STEP_STATUS_META[step.status];
          const risk =
            step.kind === 'write' && step.action ? RISK_META[actionRisk(step.action)] : null;
          const gated =
            step.status === 'awaiting_approval' && !!step.approvalId && inFlight;
          const deciding = decidingStepId === step.id;
          return (
            <div
              key={step.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                padding: '6px 0',
                borderTop: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <span className={kind.pill} style={{ fontSize: 10, padding: '2px 8px' }}>
                {kind.label}
              </span>
              <span className={status.pill} style={{ fontSize: 10, padding: '2px 8px' }}>
                {status.label}
              </span>
              {risk && (
                <span
                  className={risk.pill}
                  title="Risk tier for this write — shown before you approve; execution is unchanged."
                  style={{ fontSize: 10, padding: '2px 8px' }}
                >
                  {risk.label}
                </span>
              )}
              <span className="t-meta" style={{ color: C.text, flex: 1, minWidth: 160 }}>
                {step.description}
              </span>
              {step.result && (
                <span className="t-meta" style={{ color: C.textMuted }}>
                  {step.result}
                </span>
              )}
              {gated && canMutate && (
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  <button
                    type="button"
                    className="glass-btn glass-btn--primary"
                    onClick={() => onDecide(step, 'approved')}
                    disabled={deciding || approving}
                    title="Approve — executes this write and the run continues."
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  >
                    <Check size={12} /> Approve
                  </button>
                  <button
                    type="button"
                    className="glass-btn"
                    onClick={() => onDecide(step, 'dismissed')}
                    disabled={deciding || approving}
                    title="Dismiss — skips this write and the run continues."
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  >
                    <X size={12} /> Dismiss
                  </button>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </GlassPanel>
  );
}
